import { JSX, useEffect, useMemo, useState } from "react";

import { useTheme } from "@mui/material/styles";
import { DateView, LocalizationProvider, type PickersCalendarHeaderProps } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useTranslation } from "react-i18next";

import { localeMap } from "./localeMap";
import PickerHeader from "./PickerHeader";
import { slotProps } from "./slotProps";
import WeekContext from "./WeekContext";
import WeekDayComponent from "./WeekDayComponent";
import "../../locales/dayjs-custom-locales";
import WeekHoverContext from "./WeekHoverContext";
import WeekTextField from "./WeekTextField";

dayjs.extend(isoWeek);
dayjs.updateLocale("en", { weekStart: 1 });

const MIN_DATE = dayjs().add(-16, "year").startOf("year");
const MAX_DATE = dayjs().add(1, "year").endOf("year");

export type DatePickerVariant = "day" | "week" | "month" | "year";

export interface DatePickerProps {
  variant?: DatePickerVariant;
  value: Dayjs;
  onChange: (value: Dayjs) => void;
}

function DatePickerCustom({ variant = "day", value, onChange }: DatePickerProps): JSX.Element {
  const theme = useTheme();
  const {
    i18n: { language },
  } = useTranslation();

  const defaultViewByVariant: Record<DatePickerVariant, "day" | "month" | "year"> = {
    day: "day",
    week: "day",
    month: "month",
    year: "year",
  };

  const [view, setView] = useState<"day" | "month" | "year">(defaultViewByVariant[variant]);

  const handleOpen = () => {
    setView(defaultViewByVariant[variant]);
  };

  useEffect(() => {
    dayjs.locale(language);
  }, [language]);

  const handleDayChange = (date: Dayjs | null) => {
    if (date) onChange(date);
  };

  const handleWeekChange = (date: Dayjs | null) => {
    if (!date) return;
    const start = dayjs(date).startOf("week");
    onChange(start);
  };

  const handleViewChange = (newView: "day" | "month" | "year") => {
    const defaultView = defaultViewByVariant[variant];

    if (view === newView) {
      setView(defaultView);
      return;
    }
    setView(newView);
  };

  const formatByVariant = {
    day: "DD MMM YYYY",
    month: "MMMM YYYY",
    year: "YYYY",
    week: "DD MMM YYYY",
  };

  const formatWeekLabel = (date: Dayjs) => {
    const start = date.startOf("week");
    const end = date.endOf("week");
    return `${start.format("D MMM")} - ${end.format("D MMM YYYY")}`;
  };

  const config = {
    day: {
      openTo: "day",
      views: ["day"] as const,
      onChange: handleDayChange,
      slots: {
        toolbar: () => null,
        calendarHeader: (props: PickersCalendarHeaderProps) => (
          <PickerHeader
            currentMonth={props.currentMonth}
            onMonthChange={props.onMonthChange}
            onViewChange={(view: "day" | "month" | "year") => props.onViewChange?.(view)}
            variant='day'
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            disableFuture={true}
          />
        ),
      },
    },

    week: {
      openTo: "day",
      views: ["day"] as const,
      onChange: handleWeekChange,
      slots: {
        day: WeekDayComponent,
        toolbar: () => null,
        calendarHeader: (props: PickersCalendarHeaderProps) => (
          <PickerHeader
            currentMonth={props.currentMonth}
            onMonthChange={props.onMonthChange}
            onViewChange={(view: "day" | "month" | "year") => props.onViewChange?.(view)}
            variant='day'
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            disableFuture={true}
          />
        ),
      },
    },

    month: {
      openTo: "month",
      views: ["month"] as const,
      onChange: handleDayChange,
      slots: {
        toolbar: () => null,
        calendarHeader: (props: PickersCalendarHeaderProps) => (
          <PickerHeader
            currentMonth={props.currentMonth}
            onMonthChange={props.onMonthChange}
            onViewChange={(view: "day" | "month" | "year") => props.onViewChange?.(view)}
            variant='month'
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            disableFuture={true}
          />
        ),
      },
    },

    year: {
      openTo: "year",
      views: ["year"] as const,
      onChange: handleDayChange,
      slots: {
        toolbar: () => null,
        calendarHeader: () => null,
      },
    },
  };

  const { views, slots, onChange: handler } = config[variant];

  const memoizedSlots = useMemo(() => {
    return {
      ...slots,
      textField: variant === "week" ? WeekTextField : undefined,
    };
  }, [slots, variant]);
  const memoizedSlotProps = useMemo(() => {
    const base = slotProps(theme, variant, view);

    return {
      ...base,
      textField:
        variant === "week"
          ? {
            inputProps: {
              readOnly: true,
              value: formatWeekLabel(value),
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => e.preventDefault(),
            },
          }
          : {},
    };
  }, [theme, variant, view, value]);

  const [hover, setHover] = useState<Dayjs | null>(null);
  const hoverContext = useMemo(() => ({ hover, setHover }), [hover]);
  const currentViews = (view === "month" ? ["month"] : view === "year" ? ["year"] : views) as readonly DateView[];

  const picker = (
    <DatePicker
      enableAccessibleFieldDOMStructure={false}
      value={value}
      onChange={handler}
      onOpen={handleOpen}
      view={view}
      onViewChange={handleViewChange}
      views={currentViews}
      dayOfWeekFormatter={(day) => dayjs(day).format("dd")}
      format={formatByVariant[variant]}
      slots={memoizedSlots}
      slotProps={memoizedSlotProps}
      disableFuture
      minDate={MIN_DATE}
      maxDate={MAX_DATE}
      yearsPerRow={3}
    //displayWeekNumber
    />
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language} localeText={localeMap[language]}>
      {variant === "week" ? (
        <WeekContext.Provider value={value}>
          <WeekHoverContext.Provider value={hoverContext}>{picker}</WeekHoverContext.Provider>
        </WeekContext.Provider>
      ) : (
        picker
      )}
    </LocalizationProvider>
  );
}

export default DatePickerCustom;
